// assets/sticky-atc.js
import { fetchConfig } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';

(() => {
    const NAME = 'sticky-atc-form';
    if (customElements.get(NAME)) return;

    class StickyATC extends HTMLElement {
        #io = null;
        #abort = new AbortController();
        #inFlight = false;

        // Gate visibility so it never shows on initial load and always hides when you scroll back up
        #hasUserScrolled = false;
        #hasCrossedAnchor = false;

        connectedCallback() {
            this.__placeholder = document.createComment('sticky-atc');
            this.parentNode?.insertBefore(this.__placeholder, this);

            this.sectionId = this.dataset.sectionId;
            this.productId = this.dataset.productId;
            this.primaryFormId = this.dataset.primaryFormId;
            this.anchorSelector = this.dataset.anchorSelector || '[data-sticky-atc-anchor]';

            this.sectionRoot =
                document.querySelector(`.shopify-section[id*="${this.sectionId}"]`) ||
                this.closest('.shopify-section') ||
                document.querySelector('.shopify-section');

            this.anchor =
                document.querySelector(this.anchorSelector) ||
                this.sectionRoot?.querySelector(this.anchorSelector);

            // Sticky refs
            this.variants = this.#parseVariantsJSON();
            this.optionSelects = Array.from(this.querySelectorAll('[data-sticky-option]'));
            this.form = this.querySelector('[data-sticky-form]');
            this.variantIdInput = this.querySelector('[data-sticky-variant-id]');
            this.qtyInput = this.querySelector('[data-sticky-qty]');
            this.deliveryInput = this.querySelector('[data-sticky-delivery]');
            this.submitBtn = this.querySelector('[data-sticky-submit]');
            this.priceEl = this.querySelector('[data-sticky-price]');

            // Primary refs
            this.primaryForm = document.getElementById(this.primaryFormId);
            this.primaryVariantIdInput = this.primaryForm?.querySelector('input[name="id"]');
            this.primaryQtyInput = this.primaryForm?.querySelector('input[name="quantity"]');
            this.primaryDeliveryInput =
                this.primaryForm?.querySelector('input[name="properties[Estimated Delivery]"]') ||
                document.querySelector('#delivery-stamp');

            if (!this.anchor || !this.form || !this.variantIdInput || !this.submitBtn || !this.variants?.length) return;

            // Always start hidden
            this.hide();

            // Track user scroll (prevents showing on load)
            const onScroll = () => {
                if (window.scrollY > 10) this.#hasUserScrolled = true;
            };
            onScroll();
            window.addEventListener('scroll', onScroll, { passive: true, signal: this.#abort.signal });

            // Anchor observer:
            // - when anchor visible => hide (always)
            // - when anchor not visible => show only if user scrolled AND anchor has been seen once AND we are below the anchor
            this.#io = new IntersectionObserver(
                (entries) => {
                    const entry = entries[0];
                    if (!entry) return;

                    const isIntersecting = entry.isIntersecting;

                    // If we can see the primary buttons, sticky must be hidden (fixes "scroll up keeps active")
                    if (isIntersecting) {
                        this.#hasCrossedAnchor = true;
                        this.hide();
                        return;
                    }

                    // Not intersecting:
                    // Never show unless user has scrolled and has reached the anchor once
                    if (!this.#hasUserScrolled || !this.#hasCrossedAnchor) {
                        this.hide();
                        return;
                    }

                    // Extra safety: only show if we are actually below the anchor
                    const rect = this.anchor.getBoundingClientRect();
                    const anchorIsAboveViewport = rect.bottom < 0;

                    if (anchorIsAboveViewport) {
                        this.show();
                    } else {
                        // This covers the case where the anchor is below fold (page load) or we are scrolling upward toward it
                        this.hide();
                    }
                },
                {
                    threshold: 0,
                    rootMargin: '0px 0px 0px 0px',
                }
            );
            this.#io.observe(this.anchor);

            // Sticky option change -> update sticky variant -> push to primary
            this.optionSelects.forEach((sel) => sel.addEventListener('change', this.#onStickyOptionsChange));

            // Sync qty + delivery
            this.qtyInput?.addEventListener('change', this.#pushQtyToPrimary);
            this.primaryQtyInput?.addEventListener('change', this.#pullQtyFromPrimary, { signal: this.#abort.signal });

            // Submit lock
            this.form.addEventListener('submit', this.#onSubmit);

            // Variant updates
            const target = this.sectionRoot || document;
            target.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal: this.#abort.signal });

            // Initial sync
            this.syncFromPrimary();
        }

        disconnectedCallback() {
            this.#abort.abort();
            if (this.#io) this.#io.disconnect();

            if (this.__placeholder?.parentNode) {
                this.__placeholder.parentNode.insertBefore(this, this.__placeholder);
                this.__placeholder.remove();
            }
        }

        show() {
            this.classList.add('is-visible');
            this.setAttribute('aria-hidden', 'false');
            this.syncFromPrimary();
        }

        hide() {
            this.classList.remove('is-visible');
            this.setAttribute('aria-hidden', 'true');
        }

        #parseVariantsJSON() {
            try {
                const script = this.querySelector('[data-sticky-variants]');
                return script ? JSON.parse(script.textContent) : [];
            } catch {
                return [];
            }
        }

        // ---------- SYNC ----------

        syncFromPrimary() {
            const primaryId = this.primaryVariantIdInput?.value;
            if (primaryId) this.#applyVariantById(Number(primaryId));

            this.#pullQtyFromPrimary();
            this.#pullDeliveryFromPrimary();

            this.#syncButtonStateFromPrimary();
        }

        #pullQtyFromPrimary = () => {
            if (!this.qtyInput) return;
            const q = this.primaryQtyInput?.value;
            if (q && this.qtyInput.value !== q) this.qtyInput.value = q;
        };

        #pushQtyToPrimary = () => {
            if (!this.primaryQtyInput || !this.qtyInput) return;
            if (this.primaryQtyInput.value !== this.qtyInput.value) {
                this.primaryQtyInput.value = this.qtyInput.value;
                this.primaryQtyInput.dispatchEvent(new Event('change', { bubbles: true }));
            }
        };

        #pullDeliveryFromPrimary() {
            if (!this.deliveryInput) return;
            const d = this.primaryDeliveryInput?.value || '';
            if (this.deliveryInput.value !== d) this.deliveryInput.value = d;
        }

        #syncButtonStateFromPrimary() {
            const primaryBtn =
                this.primaryForm?.querySelector('button[name="add"]') ||
                this.sectionRoot?.querySelector('[ref="addToCartButton"]') ||
                this.sectionRoot?.querySelector('button[name="add"]');

            if (!primaryBtn) return;

            const isDisabled =
                primaryBtn.hasAttribute('disabled') ||
                primaryBtn.getAttribute('aria-disabled') === 'true' ||
                primaryBtn.disabled === true;

            if (isDisabled) this.submitBtn.setAttribute('disabled', 'true');
            else this.submitBtn.removeAttribute('disabled');
        }

        #onVariantUpdate = (event) => {
            if (event?.detail?.data?.productId && String(event.detail.data.productId) !== String(this.productId)) return;

            const variant = event?.detail?.resource;
            if (!variant?.id) return;

            this.#applyVariantById(Number(variant.id));
            this.#pullDeliveryFromPrimary();
            this.#pullQtyFromPrimary();
            this.#syncButtonStateFromPrimary();
        };

        #onStickyOptionsChange = () => {
            const selected = this.#getSelectedOptions();
            const match = this.#findVariantByOptions(selected);

            if (!match) {
                this.submitBtn.setAttribute('disabled', 'true');
                return;
            }

            this.#applyVariant(match);
            this.#pushOptionsToPrimary(selected);
        };

        #getSelectedOptions() {
            const byPos = new Map();
            this.optionSelects.forEach((sel) => {
                const pos = Number(sel.dataset.optionPosition || '0');
                byPos.set(pos, sel.value);
            });
            const positions = Array.from(byPos.keys()).sort((a, b) => a - b);
            return positions.map((p) => byPos.get(p));
        }

        #findVariantByOptions(selectedOptions) {
            return this.variants.find((v) => {
                const opts = [v.option1, v.option2, v.option3].filter(Boolean);
                if (opts.length !== selectedOptions.length) return false;
                return opts.every((val, i) => String(val) === String(selectedOptions[i]));
            });
        }

        #applyVariantById(id) {
            const v = this.variants.find((x) => Number(x.id) === Number(id));
            if (v) this.#applyVariant(v);
        }

        #applyVariant(variant) {
            this.variantIdInput.value = variant.id;

            const values = [variant.option1, variant.option2, variant.option3].filter(Boolean);
            this.optionSelects.forEach((sel) => {
                const pos = Number(sel.dataset.optionPosition || '0');
                const val = values[pos - 1];
                if (val != null && sel.value !== val) sel.value = val;
            });

            if (variant.available === false) this.submitBtn.setAttribute('disabled', 'true');
            else this.submitBtn.removeAttribute('disabled');
        }

        #pushOptionsToPrimary(selectedOptions) {
            if (!this.sectionRoot) return;

            const primaryOptionSelects = Array.from(this.sectionRoot.querySelectorAll('select[name^="options["]'));
            if (primaryOptionSelects.length) {
                primaryOptionSelects.forEach((sel, idx) => {
                    const val = selectedOptions[idx];
                    if (val != null && sel.value !== val) {
                        sel.value = val;
                        sel.dispatchEvent(new Event('change', { bubbles: true }));
                    }
                });
                return;
            }

            selectedOptions.forEach((val, idx) => {
                const optionIndex = idx + 1;
                const radio = this.sectionRoot.querySelector(
                    `input[type="radio"][data-option-position="${optionIndex}"][value="${CSS.escape(val)}"]`
                );
                if (radio && !radio.checked) {
                    radio.checked = true;
                    radio.dispatchEvent(new Event('change', { bubbles: true }));
                }
            });
        }

        // ---------- SUBMIT ----------

        #onSubmit = (event) => {
            event.preventDefault();
            if (this.#inFlight) return;

            this.#pullDeliveryFromPrimary();
            this.#pullQtyFromPrimary();

            this.#inFlight = true;
            this.submitBtn.setAttribute('disabled', 'true');

            const formData = new FormData(this.form);

            const cartItemsComponents = document.querySelectorAll('cart-items-component');
            const sectionIds = [];
            cartItemsComponents.forEach((item) => {
                if (item instanceof HTMLElement && item.dataset.sectionId) sectionIds.push(item.dataset.sectionId);
            });
            if (sectionIds.length) formData.append('sections', sectionIds.join(','));

            const fetchCfg = fetchConfig('javascript', { body: formData });

            fetch(Theme.routes.cart_add_url, {
                ...fetchCfg,
                headers: { ...fetchCfg.headers, Accept: 'text/html' },
            })
                .then((r) => r.json())
                .then((response) => {
                    if (response.status) {
                        this.dispatchEvent(
                            new CartErrorEvent(this.form.getAttribute('id') || '', response.message, response.description, response.errors)
                        );
                        return;
                    }

                    const id = formData.get('id');
                    this.dispatchEvent(
                        new CartAddEvent({}, id?.toString() || '', {
                            source: 'sticky-atc',
                            itemCount: Number(formData.get('quantity')) || 1,
                            productId: this.productId,
                            sections: response.sections,
                        })
                    );
                })
                .catch(console.error)
                .finally(() => {
                    this.#inFlight = false;

                    const v = this.variants.find((x) => String(x.id) === String(this.variantIdInput.value));
                    if (v?.available === false) this.submitBtn.setAttribute('disabled', 'true');
                    else this.submitBtn.removeAttribute('disabled');

                    cartPerformance.measureFromEvent('add:user-action', event);
                });
        };
    }

    customElements.define(NAME, StickyATC);
})();
